/**
 * Which rate cards a strategy's scope fits (CU-869fh646w, UX decision D4).
 *
 * A strategy decides nothing unless some rate card falls inside its
 * sites and meets its tariff-type and insurance filters — and the
 * wizard let an operator scope a rule so tightly that no card did,
 * without a word. This is the rule, pure, so the wizard block, the
 * footer line and the table column all read the same answer from the
 * same two lists the page already holds: the faces (`GET
 * /setup/rate-cards/faces`) and the form.
 *
 *   - site: the card's site must be inside the strategy's sites (empty
 *     = every site; exclusion = not in the list);
 *   - tariff type and insurance, per axis: a card that leaves the axis
 *     blank fits any filter (blank means any since this task); a pinned
 *     card must meet the filter (empty filter = fits; exclusion = not in
 *     the list);
 *   - offices, dates, durations and categories are not judged: a rate
 *     card has none of them (Curro, answer 2). Fitting is necessary,
 *     not enough — those still decide on each pass.
 *
 * Every rate card counts: there is no "disabled" rate card (UX D10).
 *
 * `idList` / `tariffTypeList` are the adapters' own, so the block reads
 * the form with the same normalisation the save does.
 */
import { idList, tariffTypeList } from './adapters.js'

const present = (v) => v !== null && v !== undefined && v !== ''

/** Whether one rate card (`{site_id, is_prepaid, ps_insurance_id}`) fits the form's scope. */
export function faceFits(face, form) {
	if (!face) return false
	const f = form || {}
	const sites = idList(f.filterGroupSites)
	if (sites.length && present(face.site_id)
	    && (sites.includes(Number(face.site_id)) === !!f.filterGroupSitesExclusion)) return false
	// No exclusion flag on this axis: "exclude Prepaid" is "Non prepaid"
	// said another way (`tariffTypeList`).
	const tariffs = tariffTypeList(f.filterTariffTypes)          // ['1'] | ['0'] | []
	if (tariffs.length === 1 && present(face.is_prepaid)
	    && !tariffs.includes(Number(face.is_prepaid) ? '1' : '0')) return false
	const ins = idList(f.filterInsurances)
	if (ins.length && present(face.ps_insurance_id)
	    && (ins.includes(Number(face.ps_insurance_id)) === !!f.filterInsurancesExclusion)) return false
	return true
}

/** The rate cards of the list that fit, in the list's order. */
export const rateCardsThatFit = (faces, form) => (Array.isArray(faces) ? faces : []).filter(f => faceFits(f, form))

/**
 * The scope, said in one line for the "none fits" alert: `Sites:
 * DoYouSpain · Tariff type: Non prepaid · Insurance: all.` Names come
 * from the catalogues passed in (`labelOf`, `prepaidName`,
 * `insuranceName`), never from literals.
 */
export function fitScopeSummary(form, { labelOf, prepaidName, insuranceName }) {
	const f = form || {}
	const sites = idList(f.filterGroupSites)
	const tariffs = tariffTypeList(f.filterTariffTypes)
	const ins = idList(f.filterInsurances)
	const listOf = (ids, name, exclusion) => {
		if (!ids.length) return 'all'
		const names = ids.map(name).join(', ')
		return exclusion ? `all except ${names}` : names
	}
	return [
		`Sites: ${listOf(sites, labelOf, f.filterGroupSitesExclusion)}`,
		`Tariff type: ${tariffs.length === 1 ? prepaidName(tariffs[0] === '1') : 'all'}`,
		`Insurance: ${listOf(ins, insuranceName, f.filterInsurancesExclusion)}`,
	].join(' · ')
}
